import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';

function readKeyboardInset() {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
}

/** Bottom inset when iOS keyboard is visible (resize mode is none). */
export function useKeyboardInset() {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const cleanups: Array<() => void> = [];
    const sync = (value?: number) => {
      if (!cancelled) setInset(value ?? readKeyboardInset());
    };

    const viewport = window.visualViewport;
    if (viewport) {
      const onViewportChange = () => sync();
      viewport.addEventListener('resize', onViewportChange);
      viewport.addEventListener('scroll', onViewportChange);
      cleanups.push(
        () => viewport.removeEventListener('resize', onViewportChange),
        () => viewport.removeEventListener('scroll', onViewportChange),
      );
    }

    void (async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        const show = await Keyboard.addListener('keyboardWillShow', (info) => {
          sync(info.keyboardHeight);
        });
        const hide = await Keyboard.addListener('keyboardWillHide', () => {
          sync(0);
        });
        cleanups.push(() => void show.remove(), () => void hide.remove());
      } catch {
        /* keyboard plugin unavailable */
      }
    })();

    return () => {
      cancelled = true;
      cleanups.forEach((fn) => fn());
    };
  }, []);

  return inset;
}
